import React from 'react';

import {
  CropIcon, BoundingBoxIcon, EyeOnIcon, EyeOffIcon, DeleteIcon,
} from '@vlabs/icons';
import { Control, Tooltip } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './ZoneItem.module.sass';

const config = {
  roi: {
    icon: <CropIcon />,
    label: 'ROI',
  },
  droi: {
    icon: <BoundingBoxIcon />,
    label: 'DROI',
  },
};

interface ZoneItemProps {
  regionType?: 'roi' | 'droi';
  layerNumber?: number;
  color?: 'positive' | 'accentOrange' | 'accentBlue' | 'accentRed' | 'alwaysBlack';
  isVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  onZoneClick?: VoidFunction;
  onChangeVisibility?: VoidFunction;
  onRemove?: VoidFunction;
  prefixTestId?: string;
  canHide?: boolean;
}

export function ZoneItem({
  regionType = 'roi',
  layerNumber = undefined,
  color = 'positive',
  isVisible = false,
  isSelected = false,
  isDisabled = false,
  onZoneClick = undefined,
  onChangeVisibility = undefined,
  onRemove = undefined,
  prefixTestId = undefined,
  canHide = true,
}: ZoneItemProps): React.JSX.Element {
  const { t } = useTranslation();

  const layerNumberForLabel = regionType === 'droi' && layerNumber ? ` ${layerNumber}` : '';
  const label = config[regionType].label + layerNumberForLabel;
  const rootDataTestId = regionType === 'droi' ? `zoneItem.droi.${layerNumber}` : 'zoneItem.roi';

  return (
    <button
      aria-label={`${label} layer select button`}
      className={cn(st.Root, {
        [st.Root_hide]: !isVisible,
        [st.Root_selected]: isSelected,
      })}
      data-testid={prefixTestId ? `${prefixTestId}.${rootDataTestId}` : rootDataTestId}
      disabled={isDisabled}
      onClick={onZoneClick}
      type="button"
    >
      <span className={st.Content}>
        <span
          className={cn(st.Icon, {
            [st[`Icon_${color}`]]: !isDisabled,
          })}
        >
          {config[regionType].icon}
        </span>

        <span className={st.Label}>
          {label}
        </span>
      </span>

      <span className={st.Actions}>
        {!!canHide && (
          <Tooltip
            position="bottom"
            tooltipContent={t(`кнопка.${isVisible ? 'скрыть' : 'показать'}`)}
          >
            <Control.IconButton
              data-testid={`${rootDataTestId}.visibilityButton`}
              icon={isVisible ? <EyeOffIcon /> : <EyeOnIcon />}
              kind="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onChangeVisibility?.();
              }}
              size="small"
              variant="flat"
            />
          </Tooltip>
        )}

        {regionType === 'droi' && (
          <Tooltip
            position="bottom"
            tooltipContent={t('кнопка.удалить')}
          >
            <Control.IconButton
              data-testid={`${rootDataTestId}.removeButton`}
              icon={<DeleteIcon />}
              kind="negative"
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.();
              }}
              size="small"
              variant="flat"
            />
          </Tooltip>
        )}
      </span>
    </button>
  );
}
